// $lib/stores/tableHistoryStore.ts
import { writable } from "svelte/store";
import { browser } from "$app/environment";
import { authStore } from "./authStore";
import type { User } from "$lib/types";
import type { TableInfo } from "$lib/types";

export interface GroupedTables {
  [key: string]: TableInfo[];
}

export class TableHistoryManager {
  public static readonly STORAGE_KEY = "tableHistory";
  public static readonly ANONYMOUS_USER = "anonymous";

  static getUserIdentifier(user: User | undefined): string {
    if (!user) return this.ANONYMOUS_USER;
    return user.id || user.email || this.ANONYMOUS_USER;
  }

  static mergeTables(existing: TableInfo[], incoming: TableInfo[]): TableInfo[] {
    const tableMap = new Map<string, TableInfo>();

    existing.forEach((table) => {
      const existingTable = {
        ...table,
        createdAt: table.createdAt instanceof Date ? table.createdAt : new Date(table.createdAt),
        updatedAt: table.updatedAt instanceof Date ? table.updatedAt : new Date(table.updatedAt),
      };
      tableMap.set(existingTable.publicId, existingTable);
    });

    incoming.forEach((table) => {
      const incomingTable = {
        ...table,
        createdAt: table.createdAt instanceof Date ? table.createdAt : new Date(table.createdAt),
        updatedAt: table.updatedAt instanceof Date ? table.updatedAt : new Date(table.updatedAt),
      };

      const existingTable = tableMap.get(incomingTable.publicId);

      if (!existingTable || incomingTable.updatedAt.getTime() > existingTable.updatedAt.getTime()) {
        tableMap.set(incomingTable.publicId, incomingTable);
      }
    });

    return Array.from(tableMap.values()).sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  }

  static getTimeGroupLabel(date: Date): string {
    if (isNaN(date.getTime())) return "Other";

    const timeFrames = this.getTimeFrames();

    if (date >= timeFrames.today) return "Today";
    if (date >= timeFrames.yesterday) return "Yesterday";
    if (date >= timeFrames.lastWeek) return "Last Week";
    if (date >= timeFrames.lastMonth) return "Last Month";

    return date.toLocaleString("default", { month: "long", year: "numeric" });
  }

  private static getTimeFrames() {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    return { today, yesterday, lastWeek, lastMonth };
  }

  static saveToLocalStorage(tables: TableInfo[]): void {
    if (!browser) return;

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tables));
    } catch (error) {
      console.error("Error saving table history:", error);
    }
  }
}

const { subscribe, update } = writable<TableInfo[]>([]);

export const tableHistoryStore = {
  subscribe,

  setTables(tables: TableInfo[]) {
    update((existingTables) => {
      const userId = TableHistoryManager.getUserIdentifier(authStore.getUser());

      // Keep the tables as they are, without modifying ownerId
      const newTables = tables.map((table) => ({
        ...table,
      }));

      // TableHistoryManager.saveToLocalStorage(newTables);
      return newTables;
    });
  },

  appendTables(newTables: TableInfo[]) {
    update((existingTables) => {
      const userId = TableHistoryManager.getUserIdentifier(authStore.getUser());

      const processedNewTables = newTables.map((table) => ({
        ...table,
        ownerId: userId,
      }));

      const mergedTables = TableHistoryManager.mergeTables(existingTables, processedNewTables);
      // TableHistoryManager.saveToLocalStorage(mergedTables);
      return mergedTables;
    });
  },

  removeTable(publicId: string) {
    update((tables) => {
      const updatedTables = tables.filter((t) => t.publicId !== publicId);
      // TableHistoryManager.saveToLocalStorage(updatedTables);
      return updatedTables;
    });
  },

  getTablesForCurrentUser(): TableInfo[] {
    const userId = TableHistoryManager.getUserIdentifier(authStore.getUser());
    let tables: TableInfo[] = [];

    const unsubscribe = subscribe((currentTables) => {
      tables = currentTables.filter((table) => table.ownerId === userId);
    });

    unsubscribe();
    return tables;
  },

  updateTableName(tableId: string, newName: string) {
    update((tables) => {
      return tables.map((table) =>
        table.publicId === tableId ? { ...table, name: newName, updatedAt: new Date() } : table,
      );
    });
  },
};

export function groupTables(tables: TableInfo[]): GroupedTables {
  const sortedTables = [...tables].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const sections: { [key: string]: TableInfo[] } = {};

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  sortedTables.forEach((table) => {
    const tableDate = new Date(table.updatedAt);
    tableDate.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const lastWeekStart = new Date(today);
    lastWeekStart.setDate(today.getDate() - 7);

    const lastMonthStart = new Date(today);
    lastMonthStart.setMonth(today.getMonth() - 1);

    let section: string;
    if (tableDate.getTime() >= today.getTime()) {
      section = "Today";
    } else if (tableDate.getTime() >= yesterday.getTime()) {
      section = "Yesterday";
    } else if (tableDate.getTime() >= lastWeekStart.getTime()) {
      section = "Last week";
    } else if (tableDate.getTime() >= lastMonthStart.getTime()) {
      section = "Last month";
    } else {
      section = "Older";
    }

    if (!sections[section]) {
      sections[section] = [];
    }
    sections[section].push(table);
  });

  const sectionPriority = {
    Today: 0,
    Yesterday: 1,
    "Last week": 2,
    "Last month": 3,
    Older: 4,
  } as Record<string, number>;

  return Object.fromEntries(
    Object.entries(sections).sort((a, b) => {
      const aPriority = sectionPriority[a[0]] ?? 5;
      const bPriority = sectionPriority[b[0]] ?? 5;
      return aPriority - bPriority;
    }),
  );
}
